// ---------------------------------------------------------------------------
// coachBus - the one-way channel from product pages to the guided-tour engine.
//
// A page that just saved something announces it and moves on:
//
//     import { coach } from '../../onboarding/coachBus';
//     coach.emit(COACH_EVENT.memberCreated, { memberId: res.data.id });
//
// The page never imports guide code and has no idea a tour is running. Guides
// subscribe by event name, so onboarding logic stays in the onboarding folder
// instead of being smeared across two hundred screens.
//
// Emitting is always safe: with no tour running, emit is a no-op.
// ---------------------------------------------------------------------------

import { isKnownCoachEvent } from './events';

const listeners = new Map(); // name -> Set<handler>

// Last emit per event name, so a guide that subscribes a beat late (its target
// was still painting when the save landed) does not miss its advance signal.
// Only read through consumeRecent, which enforces the freshness window.
const recent = new Map(); // name -> timestamp
const RECENT_WINDOW_MS = 4000;

function subscribe(name, handler) {
  if (!listeners.has(name)) listeners.set(name, new Set());
  listeners.get(name).add(handler);
  return () => {
    const set = listeners.get(name);
    if (!set) return;
    set.delete(handler);
    if (set.size === 0) listeners.delete(name);
  };
}

function emit(name, payload) {
  if (!name) return;
  if (process.env.NODE_ENV === 'development' && !isKnownCoachEvent(name)) {
    // Not fatal - a typo here should never break a save flow - but it means a
    // guide waiting on this name will hang until its stall timeout.
    console.warn(`[coach] emitted unregistered event "${name}". Add it to onboarding/events.js.`);
  }
  recent.set(name, Date.now());
  const set = listeners.get(name);
  if (!set) return;
  // Copy first: a handler that advances the tour will unsubscribe mid-iteration.
  Array.from(set).forEach((handler) => {
    try {
      handler(payload, name);
    } catch (err) {
      console.error(`[coach] listener for "${name}" threw`, err);
    }
  });
}

// True if `name` fired within the freshness window; consumes it either way so a
// single save can only satisfy one step.
function consumeRecent(name) {
  const at = recent.get(name);
  if (!at) return false;
  recent.delete(name);
  return Date.now() - at <= RECENT_WINDOW_MS;
}

function reset() {
  recent.clear();
}

export const coach = { emit, subscribe, consumeRecent, reset };

export default coach;
